/* Source-backed Shimano document and part lookup. No inferred compatibility. */
(() => {
  'use strict';
  const norm = value => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const bucket = value => { let hash = 0; for (const c of norm(value)) hash = (hash * 31 + c.charCodeAt(0)) % 64; return String(hash).padStart(2, '0'); };
  const el = (tag, text, cls) => { const node = document.createElement(tag); if (text != null) node.textContent = text; if (cls) node.className = cls; return node; };
  const link = (text, url, cls, external = false) => { const a = el('a', text, cls); a.href = url; if (external) { a.target = '_blank'; a.rel = 'noopener noreferrer'; } return a; };
  const cached = new Map();
  async function json(url) { if (!cached.has(url)) cached.set(url, fetch(url, { credentials: 'same-origin' }).then(r => { if (!r.ok) throw new Error('Document data could not be loaded.'); return r.json(); }).catch(e => { cached.delete(url); throw e; })); return cached.get(url); }
  const stockCache = new Map(), stockQueue = [];
  let stockActive = 0;
  function stockTask(task) {
    return new Promise((resolve, reject) => { stockQueue.push({ task, resolve, reject }); pumpStock(); });
  }
  function pumpStock() {
    while (stockActive < 4 && stockQueue.length) {
      const item = stockQueue.shift(); stockActive++;
      Promise.resolve().then(item.task).then(item.resolve, item.reject).finally(() => { stockActive--; pumpStock(); });
    }
  }
  async function productStock(variant, sku, url) {
    const key = String(variant), existing = stockCache.get(key);
    if (existing && Date.now() - existing.time < 60000) return existing.promise;
    const promise = stockTask(async () => {
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Product unavailable');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const metadata = page.querySelector('[data-shimano-product-stock]');
        if (metadata) {
          const data = JSON.parse(metadata.textContent);
          if (String(data.variant) !== key || norm(data.sku) !== norm(sku) || !data.price || !data.availability) throw new Error('Product changed');
          return data;
        }
        // Supports the previous Complete Cycles theme while previewing this release.
        const selected = page.querySelector('#VariantSelect option:checked') || page.querySelector('form.product-form [name="id"]');
        if (String(selected?.value) !== key || norm(page.querySelector('#ProductSku')?.textContent) !== norm(sku)) throw new Error('Product changed');
        const price = page.querySelector('#ProductPrice')?.textContent.trim();
        const availability = page.querySelector('#ProductStatus')?.textContent.trim();
        if (!price || !availability) throw new Error('Missing product details');
        return { variant: key, sku, price, availability };
      } finally { clearTimeout(timeout); }
    });
    stockCache.set(key, { time: Date.now(), promise });
    promise.catch(() => { if (stockCache.get(key)?.promise === promise) stockCache.delete(key); });
    return promise;
  }
  async function fillStock(node) {
    if (node.dataset.loading || !node.isConnected) return;
    node.dataset.loading = 'true';
    try {
      const stock = await productStock(node.dataset.variant, node.dataset.sku, node.dataset.url);
      node.replaceChildren(el('span', stock.price, 'sf-live-price'), el('span', stock.availability, 'sf-live-availability'));
    } catch (_) { node.textContent = 'Check product for price and availability'; }
  }
  const stockObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) { stockObserver.unobserve(entry.target); fillStock(entry.target); }
  }, { rootMargin: '250px' }) : null;
  function observeStock(parent) {
    for (const node of parent.querySelectorAll('[data-shimano-live-stock]')) {
      if (stockObserver) stockObserver.observe(node); else fillStock(node);
    }
  }

  async function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const status = root.querySelector('.sf-status'), results = root.querySelector('.sf-results'), view = root.querySelector('.sf-view');
    const searchMode = root.dataset.mode === 'search';
    const assetBase = new URL(root.dataset.index, location.href).href;
    let index, sequence = 0, activeDoc = '';
    const say = text => { status.textContent = text; };
    const error = () => { say('The parts finder could not load. Please try again or open Shimano’s documents.'); status.append(' ', link('Shimano documents ↗', 'https://si.shimano.com/en/manual/search?doctype=EV', '', true)); const old = document.querySelector('.product-exploded-view'); if (old) old.hidden = false; };
    try {
      const manifest = await json(assetBase);
      const filename = searchMode ? 'shimano-search-index.json' : `shimano-lookup-${bucket(root.dataset.sku)}.json`;
      index = await json(new URL(filename, assetBase).href);
      index.counts = manifest.counts;
    } catch (_) { if (searchMode) error(); return; }
    const docs = index.documents;
    function shopLinks(code, parent, context = {}) {
      const entries = index.parts[norm(code)]?.products || [];
      if (!entries.length) {
        const params = new URLSearchParams({ view: 'shimano-request', part: code, description: context.name || index.parts[norm(code)]?.name || '', model: context.model || '', doc: context.doc || '', from: location.pathname + location.search });
        parent.append(el('span', 'Ask us about availability', 'sf-missing'), link('REQUEST THIS PART ↗', `${index.root || ''}/search?${params}`, 'sf-buy sf-request')); return;
      }
      entries.forEach((p, i) => {
        const url = `${index.root || ''}/variants/${p.variant}`, option = el('div', null, 'sf-shop-option');
        const stock = el('div', 'Loading price and availability…', 'sf-live-stock');
        stock.dataset.shimanoLiveStock = ''; stock.dataset.variant = p.variant; stock.dataset.sku = code; stock.dataset.url = url; stock.setAttribute('aria-live', 'polite');
        option.append(stock, link(entries.length === 1 ? 'VIEW PRODUCT ↗' : `VIEW OPTION ${i + 1} ↗`, url, 'sf-buy')); parent.append(option);
      });
    }
    function options(ids, introduction) {
      results.replaceChildren(); view.replaceChildren(); activeDoc = ''; sequence++;
      if (introduction) results.append(el('p', introduction, 'sf-note'));
      const container = el('div', null, 'sf-options');
      results.append(container);
      let visible = 0;
      const more = el('button', 'SHOW MORE DIAGRAMS', 'sf-more'); more.type = 'button';
      function append() {
        const end = Math.min(visible + 40, ids.length);
        for (; visible < end; visible++) {
          const id = ids[visible], d = docs[id]; if (!d) continue;
          const b = el('button', d.label + (d.series ? ` · ${d.series}` : ''), 'sf-doc-option');
          b.type = 'button'; b.dataset.doc = id; b.setAttribute('aria-pressed', 'false');
          b.addEventListener('click', () => show(id)); container.append(b);
        }
        more.hidden = visible >= ids.length;
      }
      more.addEventListener('click', append); results.append(more); append();
      if (ids.length === 1) show(ids[0]);
    }
    function related(code, skip) {
      const ids = (index.parts[norm(code)]?.documents || []).filter(id => id !== skip);
      if (!ids.length) return null;
      const detail = el('details', null, 'sf-related'); detail.append(el('summary', `Listed in ${ids.length} other Shimano diagram${ids.length === 1 ? '' : 's'}`));
      const list = el('ul'); detail.append(list);
      ids.forEach(id => { const item = el('li'); const b = el('a', docs[id]?.label || id.replace(/-\d+$/, '')); b.href = `${index.root || ''}/search?view=shimano-parts&doc=${encodeURIComponent(id)}`; item.append(b); list.append(item); });
      return detail;
    }
    async function show(id) {
      const meta = docs[id]; if (!meta) return;
      activeDoc = id; const run = ++sequence;
      for (const b of results.querySelectorAll('[data-doc]')) b.setAttribute('aria-pressed', String(b.dataset.doc === id));
      view.replaceChildren(); say('Loading Shimano diagram…');
      const toolbar = el('div', null, 'sf-toolbar'); toolbar.append(el('h3', meta.label));
      toolbar.append(link('ORIGINAL PDF ↗', meta.pdf, '', true), link('SHIMANO SOURCE ↗', meta.source, '', true)); view.append(toolbar);
      if (meta.purchase_pdf) toolbar.append(link('PDF WITH SHOP LINKS ↗', new URL(meta.purchase_pdf, assetBase).href, '', true));
      view.append(el('p', `${meta.series || 'Shimano'}${meta.date ? ' · Document updated ' + meta.date : ''}`, 'sf-docmeta'));
      if (meta.shard == null) {
        say(''); const box = el('div', null, 'sf-pdf-only'); box.append(el('p', 'This archived diagram is available in Shimano’s original PDF. A verified interactive parts table is not yet available for this document.')); box.append(link('OPEN SHIMANO PDF ↗', meta.pdf, 'button', true)); view.append(box); return;
      }
      try {
        const url = new URL(`shimano-docs-${String(meta.shard).padStart(2, '0')}.json`, assetBase).href;
        const shard = await json(url); if (run !== sequence) return;
        const d = shard[id]; if (!d) throw new Error('Missing document.');
        say('');
        for (const p of d.parts) if (p.reference) index.parts[norm(p.code)] = p.reference;
        view.append(el('p', 'Match the model, side, cage length and dimensions shown in the document. Parts and assemblies may cover several versions.', 'sf-note'));
        const body = el('div', null, 'sf-body'); view.append(body);
        const diagramCol = el('div'); body.append(diagramCol);
        const diagram = el('div', null, 'sf-diagram'); diagramCol.append(diagram);
        const rowsCol = el('div'); body.append(rowsCol);
        const filter = el('input', null, 'sf-filter'); filter.type = 'search'; filter.placeholder = 'Filter parts in this diagram'; filter.setAttribute('aria-label', 'Filter parts in this diagram'); rowsCol.append(filter);
        const wrap = el('div', null, 'sf-tablewrap'), table = el('table'), head = el('thead'), tr = el('tr');
        for (const title of ['#', 'SHIMANO PART', 'SHOP']) tr.append(el('th', title)); head.append(tr); table.append(head); const tbody = el('tbody'); table.append(tbody); wrap.append(table); rowsCol.append(wrap);
        const rowNodes = new Map();
        d.parts.forEach((p, i) => {
          const row = el('tr'); row.id = `sf-${norm(id)}-${i}`; row.dataset.search = norm(p.code + ' ' + p.name + ' ' + p.item);
          row.append(el('td', p.item || '—')); const detail = el('td'); detail.append(el('span', p.name), el('span', p.code, 'sf-code'));
          if (p.interchange?.length) { const notes = el('div', p.interchange.map(x => `${x.model}: ${x.rank === 'A' ? 'same part (A)' : 'usable with differences (B)'}`).join('; '), 'sf-rank'); detail.append(notes); }
          const shares = related(p.code, id); if (shares) detail.append(shares); row.append(detail); const buy = el('td'); shopLinks(p.code, buy, { name: p.name, model: meta.label, doc: id }); row.append(buy); tbody.append(row);
          const code = norm(p.code); if (!rowNodes.has(code)) rowNodes.set(code, []); rowNodes.get(code).push(row);
        });
        observeStock(tbody);
        filter.addEventListener('input', () => { const query = norm(filter.value); for (const row of tbody.children) row.hidden = query && !row.dataset.search.includes(query); });
        if (d.image) {
          const img = el('img'); img.alt = `Shimano exploded diagram: ${meta.label}`; img.loading = 'lazy';
          const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg'); svg.setAttribute('aria-label', 'Clickable part callouts'); diagram.append(img, svg);
          img.addEventListener('load', () => {
            svg.setAttribute('viewBox', `0 0 ${img.naturalWidth} ${img.naturalHeight}`); svg.replaceChildren();
            for (const h of d.hotspots || []) {
              const targets = rowNodes.get(norm(h.code)); if (!targets?.length) continue;
              const a = document.createElementNS(svg.namespaceURI, 'a'); a.setAttribute('href', '#' + targets[0].id); a.setAttribute('aria-label', `Part ${h.item}: ${h.code}`);
              const rect = document.createElementNS(svg.namespaceURI, 'rect'); for (const [k,v] of Object.entries({x:h.x-4,y:h.y-4,width:h.w+8,height:h.h+8})) rect.setAttribute(k, v); a.append(rect);
              a.addEventListener('click', e => { e.preventDefault(); filter.value = ''; filter.dispatchEvent(new Event('input')); for (const r of tbody.children) r.classList.remove('sf-picked'); targets.forEach(r => r.classList.add('sf-picked')); targets[0].scrollIntoView({behavior:'smooth',block:'center'}); targets[0].tabIndex = -1; targets[0].focus({preventScroll:true}); }); svg.append(a);
            }
          });
          img.addEventListener('error', () => { diagram.replaceChildren(el('p', 'The diagram image could not load.', 'sf-image-fallback'), link('Open original PDF ↗', meta.pdf, '', true)); }); img.src = d.image;
          diagramCol.append(el('p', d.hotspots?.length ? 'Select a blue diagram callout to find its part below.' : 'Use the numbered parts list alongside the original diagram.', 'sf-note'));
        } else {
          diagram.replaceChildren(el('p', 'View the diagram in Shimano’s original PDF.', 'sf-image-fallback'), link('OPEN PDF ↗', meta.pdf, 'button', true));
        }
        if (d.method === 'pdf') rowsCol.append(el('p', 'Parts transcribed automatically from the PDF table. Consult the original PDF for complete specifications and notes.', 'sf-note'));
        if (d.parts.some(p => p.interchange?.length)) rowsCol.append(el('p', 'Shimano A: same part. B: usable with differences in material, appearance, finish or size. A blank interchangeability mark does not establish compatibility.', 'sf-note'));
      } catch (_) { if (run === sequence) error(); }
    }
    async function search(value) {
      const query = norm(value); view.replaceChildren(); results.replaceChildren(); sequence++;
      if (!query) { say(`Search ${index.counts.documents.toLocaleString()} Shimano documents by model or part number.`); return; }
      if (searchMode && !index.parts[query]) {
        const run = sequence;
        try {
          const lookup = await json(new URL(`shimano-lookup-${bucket(query)}.json`, assetBase).href);
          if (run !== sequence) return;
          Object.assign(index.parts, lookup.parts); Object.assign(docs, lookup.documents);
          const sale = lookup.sales[query];
          if (sale) { options(sale.documents, `Shimano sales SKU ${value} maps to model ${sale.model}.`); say('Choose the matching diagram.'); return; }
        } catch (_) { if (run === sequence) error(); return; }
      }
      const part = index.parts[query];
      if (part) {
        say(`${part.documents.length} Shimano diagram${part.documents.length === 1 ? '' : 's'} list this part.`);
        options(part.documents, 'Shared-part references link to the source diagram. Check the version-specific notes in each document.');
        const box = el('div', null, 'sf-current-part'); box.append(el('strong', part.name + ' · ' + part.code)); shopLinks(part.code, box); results.prepend(box); observeStock(box); return;
      }
      const exact = index.models[query]; let ids;
      if (exact) ids = exact;
      else ids = Object.keys(docs).filter(id => norm(docs[id].label).includes(query) || norm(docs[id].series).includes(query));
      ids = [...new Set(ids)].sort((a,b) => (docs[b].date || '').localeCompare(docs[a].date || '') || a.localeCompare(b));
      say(ids.length ? `${ids.length} matching Shimano document${ids.length === 1 ? '' : 's'}.` : 'No matching Shimano document was found. Try the component model printed on the product.');
      if (ids.length) options(ids, 'Choose the diagram that matches your component version.');
    }
    if (searchMode) {
      const form = root.querySelector('.sf-search'), input = form.querySelector('input'); const params = new URLSearchParams(location.search); input.value = params.get('q') || '';
      form.addEventListener('submit', e => { e.preventDefault(); search(input.value); const u = new URL(location.href); u.searchParams.set('q', input.value); u.searchParams.delete('doc'); history.replaceState({}, '', u); });
      if (params.has('doc') && docs[params.get('doc')]) { options([params.get('doc')]); } else search(input.value);
    } else {
      const sku = norm(root.dataset.sku), main = index.sales[sku], part = index.parts[sku];
      if (!main && !part) return;
      root.hidden = false;
      if (main?.documents.length) options(main.documents, `Shimano model: ${main.model}${main.spec ? ' · ' + main.spec : ''}. Select the matching service diagram.`);
      else if (part) search(sku);
      else { root.hidden = true; return; }
      // The existing exploded-view link is retained unless replacement data is present.
      if (main?.documents.some(id => docs[id]?.shard != null) || part?.documents.some(id => docs[id]?.shard != null)) {
        const old = document.querySelector('.product-exploded-view'); if (old) old.hidden = true;
      }
      const select = document.getElementById('VariantSelect'); if (select && !root.dataset.variantBound) { root.dataset.variantBound = 'true'; select.addEventListener('change', () => {
        const old = document.querySelector('.product-exploded-view'); if (old) old.hidden = false;
        root.dataset.sku = select.options[select.selectedIndex]?.dataset.sku || ''; root.dataset.initialized = ''; results.replaceChildren(); view.replaceChildren(); root.hidden = true; sequence++; init(root);
      }); }
    }
  }
  const boot = () => document.querySelectorAll('[data-shimano-finder]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
